import { AdvancedFilter, ArrowsSort, Edit } from '@saleshandy/icons';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import api from '../../../../api';

import CategoriesDropdown from '../../../../shared/components/category-dropdown/category-dropdown-container';
import type { CategoryItem } from '../../../../shared/components/category-dropdown/type';
import ExportButton from '../../../../shared/design-system/components/atoms/export-button';
import Input from '../../../../shared/design-system/components/input/input';
import { InputIconConfig } from '../../../../shared/design-system/components/input/types';
import EditCardColumnsModal from '../../../../shared/design-system/components/molecules/edit-card-column-modal/edit-card-columns-modal';
import { SHModules } from '../../../../shared/enums/module';
import { SortOrder } from '../../../../shared/enums/order';
import { KanbanViewIcon } from '../../../../shared/svg/kanban-view';
import { ListViewIcon } from '../../../../shared/svg/list-view';
import toaster, { Theme } from '../../../../shared/toaster';
import {
  executeOnRequestStatus,
  getIsRequestPending,
} from '../../../../shared/utils';
import { Permissions } from '../../../../shared/utils/access-control/enums/permissions';
import hasPermission from '../../../../shared/utils/access-control/has-permission';
import { accessibleOnClick } from '../../../../shared/utils/accessible-on-click';
import { isWhitelabelWithClientView } from '../../../../shared/utils/user-details';
import { RootState } from '../../../../store/root-reducer';
import GlobalRestrictionModal from '../../../home/components/global-restriction-modal';
import {
  BulkActionProspect,
  ProspectSortBy as ContactFilterSortBy,
  ProspectSortBy,
  ViewType,
} from '../../enums/prospect';
import { RequestStatus } from '../../../../shared/enums/request-status';
import DraggableInputModal from '../../helpers/draggable-input-modal/draggable-input-modal';
import { CategorizeByColumn } from '../../helpers/draggable-input-modal/type';
import {
  doesFieldExistInConditions,
  getSortOptionsWithFields,
} from '../../helpers/helper';
import { buildUpdatePayloadFromColumns } from '../../helpers/kanban-view-utils';
import {
  setInProgressTableCells,
  setSelectedKanbanCardsFields,
} from '../../prospect-slice';
import { KanbanProspectListPayload } from '../../types';
import BulkActions from '../prospect-list/components/bulk-actions';
import AddContactsButton from '../prospect-list/components/prospect-header/components';
import SavedFilters from '../prospect-list/components/saved-filters';
import { getProspectCustomAttributes } from '../prospect-table/utils/get-prospect-custom-attributes';
import { getKanbanProspectSystemAttributes } from '../prospect-table/utils/get-prospect-system-attributes';
import SortByOptions from '../sort-by-options';
import type { IProps } from './prospect-header-container';
import TriggerAiColumnButton from './TriggerAiColumnButton/TriggerAiColumnButton';
import { ProspectHeaderField } from './types';
import { OverlayTooltip } from '../../../../shared/design-system/components/overlay';
import AddRemoveTag from '../../../../shared/components/add-remove-tag';
import { getFormattedProspects } from '../../helpers/utils/get-formatted-prospects';
import { getInProgressFields } from '../../helpers/utils/get-inprogress-fields';
import notification from '../../../../shared/notification';
import { updateProspectListHandler } from '../../helpers/utils/update-prospect-list-handler';

const ProspectHeader: React.FC<IProps> = ({
  filters,
  savedFilters,
  setProspectFilters,
  selectedRowIds,
  isBulkActionOnRows,
  remainingProspectLimit,
  isDemoAccount,
  viewAddToSequenceModal,
  viewDeleteProspectModal,
  viewUnsubscribeProspectModal,
  viewEmailVerificationModal,
  fetchProspectListAndCount,
  viewFiltersModal,
  handleClearSelection,
  fields,
  prospects,
  getFieldsRequestStatus,
  getProspectListByIdsRequestStatus,
  getKanbanList,
  setSelectedKanbanCategoryId,
  resetFilterGroupLogic,
  sendCreateCustomFieldRequest,
  updateKanbanView,
  prospectKanbanViewData,
  prospectKanbanContactListViewData,
  createCustomFieldRequestStatus,
  resetCreateCustomFieldRequest,
  sendGetFieldsRequest,
  fetchKanbanList,
  createdKanbanViewId,
  selectedKanbanCategoryId,
  sendGetProspectListRequest,
  sendGetContactListByViewIdRequest,
  sendGetProspectListCountRequest,
  activeViewId,
  updateKanbanViewRequestStatus,
  selectedSavedView,
  setUpdatedCategoryResponse,
  deselectedIds,
  sendAddTagsToProspectsRequest,
  sendRemoveTagsFromProspectsRequest,
  addTagsToProspectsRequestStatus,
  removeTagsFromProspectsRequestStatus,
  sendAddTagsToProspectsRequestStatus,
  sendRemoveTagsFromProspectsRequestStatus,
  outcomes,
  tags,
  sendGetTagsRequest,
  sendGetProspectFiltersFieldsListRequest,
  createTaskRequestStatus,
  createTaskRequestMessage,
  createTaskRequestError,
  createCustomFieldRequestMessage,
  sendAddTagsToBulkProspectsRequest,
  sendRemoveTagsFromBulkProspectsRequest,
  sendAddTagsToBulkProspectsRequestMessage,
  sendRemoveTagsFromBulkProspectsRequestMessage,
  sendAddTagsToBulkProspectsRequestError,
  sendRemoveTagsFromBulkProspectsRequestError,
  resetBulkRequests,
  resetTags,
  resetProspectFiltersFieldsAttributesValue,
  setIsBulkProspectSelected,
  setProspectViewIdFilter,
  prospectViewIdFilter,
  setAddNewCategoryTriggered,
}) => {
  const currentViewId = activeViewId ?? selectedSavedView?.id ?? null;
  const location = useLocation();
  const restrictionModalContentRef = useRef<{
    title: string;
    description: string;
  }>({ title: '', description: '' });

  const [showGlobalRestrictionModal, setShowGlobalRestrictionModal] = useState(
    false,
  );
  const [showTagsModal, setShowTagsModal] = useState(false);

  const [searchQuery, setSearchQuery] = useState(filters.search);
  const [isSortByOpen, setIsSortByOpen] = useState(false);
  const [fieldSortOrders, setFieldSortOrders] = useState<
    Record<string, SortOrder>
  >(() => {
    if (filters.sortBy && filters.sortBy !== ProspectSortBy.None) {
      const sortByKey = String(filters.sortBy);
      return {
        [sortByKey]: filters.sortOrder || SortOrder.ASC,
      };
    }
    return {};
  });
  const [showCategorizeByModal, setShowCategorizeByModal] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [categorizeModalMode, setCategorizeModalMode] = useState<
    'create' | 'edit'
  >('create');
  const [
    categorizeModalInitialColumns,
    setCategorizeModalInitialColumns,
  ] = useState<CategorizeByColumn[]>([]);
  const [
    selectedCategory,
    setSelectedCategory,
  ] = useState<KanbanProspectListPayload | null>(null);
  const [selectedCategoryViewId, setSelectedCategoryViewId] = useState<
    number | null
  >(null);
  const [
    selectedKanbanCategoryByView,
    setSelectedKanbanCategoryByView,
  ] = useState<Record<number, number | null>>({});
  const [createdKanbanViewForViewId, setCreatedKanbanViewForViewId] = useState<{
    viewId: number | null;
    kanbanId: number | null;
  }>({ viewId: null, kanbanId: null });
  const history = useHistory();

  const sortByButtonRef = useRef(null);
  const addRemoveTagRef = useRef(null);
  const [previousSavedViewId, setPreviousSavedViewId] = useState<
    number | null | undefined
  >(undefined);

  // Edit Card Modal state
  const [editCardColumnModal, setEditCardColumnModal] = useState(false);
  const [kanbanColumnGroups, setKanbanColumnGroups] = useState([
    {
      id: 'systemAttributes',
      title: 'System Attributes',
      fields: [],
    },
    {
      id: 'CustomAttributes',
      title: 'Custom Attributes',
      fields: [],
    },
  ]);

  const [isKanbanColumnsReset, setIsKanbanColumnsReset] = useState(false);
  const [isNewKanbanColumnsAdded, setIsNewKanbanColumnsAdded] = useState(false);

  const savedNewColumn = useSelector(
    (state: RootState) => state?.prospect?.savedNewColumn,
  );

  const selectedKanbanCardsFields = useSelector(
    (state: RootState) => state?.prospect?.selectedKanbanCardsFields,
  );

  const dispatch = useDispatch();

  const availableKanbanFields = useMemo(
    () => kanbanColumnGroups.flatMap((group) => group.fields),
    [kanbanColumnGroups],
  );

  const getFieldIdFromEntry = (entry: any): string | undefined => {
    if (entry === null || entry === undefined) {
      return undefined;
    }
    if (typeof entry === 'string' || typeof entry === 'number') {
      return entry.toString();
    }

    const candidate =
      entry?.fieldId ??
      entry?.id ??
      entry?.value ??
      entry?.name ??
      entry?.label ??
      entry?.key;
    return candidate?.toString?.();
  };

  const getFieldLabelFromEntry = (entry: any, fallback?: string) => {
    if (entry === null || entry === undefined) {
      return fallback ?? '';
    }

    if (typeof entry === 'string' || typeof entry === 'number') {
      return entry.toString();
    }

    return (
      entry?.label ??
      entry?.name ??
      entry?.value ??
      entry?.title ??
      fallback ??
      ''
    );
  };

  const selectedFieldsFromView = useMemo((): ProspectHeaderField[] => {
    const selected = activeViewId
      ? prospectKanbanViewData?.view?.filterCriteria?.selectedFields
      : prospectKanbanContactListViewData?.view?.filterCriteria?.selectedFields;
    if (!Array.isArray(selected) || selected.length === 0) {
      return [];
    }

    return selected
      .map((entry) => {
        const entryId = getFieldIdFromEntry(entry);
        if (!entryId) {
          return null;
        }

        const matchedField = availableKanbanFields.find(
          (field) =>
            field?.id?.toString() === entryId ||
            field?.label?.toLowerCase() === entryId?.toLowerCase(),
        );

        if (matchedField) {
          return matchedField;
        }

        const labels = getFieldLabelFromEntry(entry, entryId);
        const label: any = fields.find((item) => String(item?.id) === labels)
          ?.label;
        return {
          id: entryId,
          label,
        };
      })
      .filter((field): field is ProspectHeaderField => Boolean(field));
  }, [
    prospectKanbanViewData,
    fields,
    activeViewId,
    availableKanbanFields,
    prospectKanbanContactListViewData,
  ]);

  const handleBulkAddTag = async (newTags) => {
    const { filterGroupLogic, search, sortBy, sortOrder } = filters;
    const payload = {
      ...(deselectedIds.length > 0 && {
        deSelectedContactIds: deselectedIds,
      }),
      ...(filterGroupLogic && { contactsFilter: filterGroupLogic }),
      ids: [],
      newTags: newTags.map((tag) => tag.name),
      ...(search !== '' && { search }),
      sortBy,
      sortOrder,
    };
    sendAddTagsToBulkProspectsRequest(payload);
  };

  const handleAddTag = async (newTags) => {
    if (isBulkActionOnRows) {
      handleBulkAddTag(newTags);
      setShowTagsModal(false);
    } else {
      sendAddTagsToProspectsRequest({
        ids: [],
        prospects: selectedRowIds,
        newTags: newTags.map((tag) => tag.name),
      });
      setShowTagsModal(false);
    }
  };

  const handleBulkRemoveTag = async (removedTags) => {
    const { filterGroupLogic, search, sortBy, sortOrder } = filters;
    const payload = {
      ...(deselectedIds.length > 0 && {
        deSelectedContactIds: deselectedIds,
      }),
      ...(filterGroupLogic && { contactsFilter: filterGroupLogic }),
      tagIds: removedTags.map((tag) => tag.id),
      ...(search !== '' && { search }),
      sortBy,
      sortOrder,
    };
    sendRemoveTagsFromBulkProspectsRequest(payload);
  };

  const handleRemoveTag = (removedTags) => {
    if (isBulkActionOnRows) {
      handleBulkRemoveTag(removedTags);
      setShowTagsModal(false);
    } else {
      const removedTagsPayload = removedTags.map((tag) => ({
        tagId: tag.id,
        prospects: selectedRowIds,
      }));
      sendRemoveTagsFromProspectsRequest(removedTagsPayload);
      setShowTagsModal(false);
    }
  };

  const handleUpdateProspectList = ({
    action,
    fetchProspectList = false,
    fetchProspectListCount = false,
    resetPageNumber = false,
  }) => {
    updateProspectListHandler({
      action,
      filters,
      setProspectFilters,
      sendGetProspectListRequest,
      sendGetProspectListCountRequest,
      fetchProspectList,
      fetchProspectListCount,
      resetPageNumber,
      resetBulkRequests,
    });
  };

  const rows = useMemo(() => {
    const formattedProspects = getFormattedProspects(prospects, outcomes);

    if (formattedProspects.length > 0) {
      const inProgressFields = getInProgressFields(formattedProspects);
      setInProgressTableCells(inProgressFields);
    }

    return getFormattedProspects(prospects, outcomes);
  }, [prospects, outcomes]);

  const renderTagIcon = () => (
    <AddRemoveTag
      ref={addRemoveTagRef}
      handleAddTag={handleAddTag}
      handleRemoveTag={handleRemoveTag}
      tags={tags}
      selectedRecords={rows
        .filter((row) => selectedRowIds.includes(row.id))
        .map((row) => ({
          ...row,
          tags: row?.prospectTags || [],
        }))}
      disabled={!isBulkActionOnRows && selectedRowIds?.length === 0}
      isShowOnlyTagIcon={true}
      isSubmitLoading={
        getIsRequestPending(sendAddTagsToProspectsRequestStatus) ||
        getIsRequestPending(sendRemoveTagsFromProspectsRequestStatus) ||
        getIsRequestPending(addTagsToProspectsRequestStatus) ||
        getIsRequestPending(removeTagsFromProspectsRequestStatus)
      }
      setShowModal={setShowTagsModal}
      showModal={showTagsModal}
      bulkSelectedOptions={{
        deSelectedEmailAccountIds: deselectedIds,
        isAllEmailAccountsSelected: isBulkActionOnRows,
        selectionCount: true,
      }}
      maxLimit={2}
      tagIconHeight={20}
      tagIconWidth={20}
    />
  );

  const handleEmailVerification = () => {
    if (isDemoAccount) {
      restrictionModalContentRef.current = {
        title: 'Email verification is disabled in demo mode',
        description:
          'This is a demo account with sample data. Signup to verify email addresses and improve your deliverability',
      };
      setShowGlobalRestrictionModal(true);
      return;
    }

    viewEmailVerificationModal();
  };

  const handleExport = async () => {
    if (isDemoAccount) {
      restrictionModalContentRef.current = {
        title: 'Export is disabled in demo mode',
        description:
          'This is a demo account with sample data. Signup to export your real leads, contacts, or reports',
      };
      setShowGlobalRestrictionModal(true);
      return;
    }

    const { filterGroupLogic, search } = filters;

    const result = await api.post(`contacts/export-async`, {
      ...{
        ...(!isBulkActionOnRows &&
          selectedRowIds?.length > 0 && { contactIds: selectedRowIds }),
        contactsFilter: filterGroupLogic,
        ...(search !== '' && { search }),
      },
    });

    const { message, status } = result.payload;

    if (status === 0) {
      toaster.error(message);
    } else if (status === 2) {
      toaster.warning(message);
    } else {
      toaster.success(
        isWhitelabelWithClientView()
          ? 'Exporting data has been started successfully. You will shortly receive a notification with the exported data.(Estimate 30 mins)'
          : message,
      );
    }
  };

  const totalConditions = useMemo(() => {
    const groups =
      prospectViewIdFilter?.filters ?? filters?.filterGroupLogic?.filters ?? [];

    return groups.reduce(
      (total, group) => total + (group?.conditions?.length || 0),
      0,
    );
  }, [prospectViewIdFilter?.filters, filters?.filterGroupLogic?.filters]);

  const handleSortByToggle = () => {
    setIsSortByOpen(!isSortByOpen);
  };

  const isSortingActive = Boolean(filters.sortBy);

  const handleProspectSorting = (
    selectedOption,
    shouldToggleOrder = false,
    specificOrder?: SortOrder,
  ) => {
    const fieldKey = String(selectedOption.key);
    const isNumericKey = /^\d+$/.test(fieldKey);
    const sortByValue = isNumericKey
      ? ((Number(fieldKey) as unknown) as ContactFilterSortBy)
      : (fieldKey as ContactFilterSortBy);

    const currentFieldSortOrder = fieldSortOrders[fieldKey] || SortOrder.ASC;
    const currentSortByString = String(filters.sortBy);
    const isSameField =
      currentSortByString === fieldKey ||
      (isNumericKey && Number(currentSortByString) === Number(fieldKey));

    // Determine new sort order
    let newSortOrder: SortOrder;
    if (fieldKey === ProspectSortBy.None) {
      newSortOrder = SortOrder.ASC;
    } else if (specificOrder !== undefined) {
      newSortOrder = specificOrder;
    } else if (shouldToggleOrder || isSameField) {
      newSortOrder =
        currentFieldSortOrder === SortOrder.ASC
          ? SortOrder.DESC
          : SortOrder.ASC;
    } else {
      newSortOrder = currentFieldSortOrder;
    }

    setFieldSortOrders({
      ...fieldSortOrders,
      [fieldKey]: newSortOrder,
    });

    setProspectFilters({
      ...filters,
      sortBy: sortByValue,
      sortOrder: newSortOrder,
      pageNum: 1,
      shouldFetchProspectList: true,
    });

    setIsSortByOpen(false);
  };

  useEffect(() => {
    executeOnRequestStatus({
      status: createCustomFieldRequestStatus,

      onSuccess: () => {
        sendGetFieldsRequest();
        fetchKanbanList();
        if (currentViewId) {
          sendGetProspectListCountRequest({
            viewType: ViewType.List,
            viewId: currentViewId,
          });
        }
      },
    });
  }, [
    createCustomFieldRequestStatus,
    fetchKanbanList,
    sendGetFieldsRequest,
    currentViewId,
    sendGetProspectListCountRequest,
  ]);
  useEffect(() => {
    executeOnRequestStatus({
      status: createTaskRequestStatus,
      onSuccess: () => {
        if (createTaskRequestMessage) {
          toaster.success(createTaskRequestMessage, {
            theme: Theme.New,
          });
        }
      },
      onFailed: () => {
        if (createTaskRequestError) {
          toaster.error(
            createTaskRequestError?.message || 'Failed to create task',
            { theme: Theme.New },
          );
        }
      },
    });
  }, [createTaskRequestStatus]);

  // Once the new Kanban view becomes available, select it in the dropdown
  useEffect(() => {
    if (
      createCustomFieldRequestStatus !== RequestStatus.Succeeded ||
      !createdKanbanViewId
    ) {
      return;
    }

    const hasNewKanbanView =
      Array.isArray(getKanbanList) &&
      getKanbanList.some((item) => item.id === createdKanbanViewId);

    if (hasNewKanbanView) {
      // Persist selection only for the current view
      setSelectedKanbanCategoryId(createdKanbanViewId);
      setSelectedCategoryViewId(currentViewId);
      setSelectedKanbanCategoryByView((prev) =>
        currentViewId !== null
          ? { ...prev, [currentViewId]: createdKanbanViewId }
          : prev,
      );
      setCreatedKanbanViewForViewId({
        viewId: currentViewId,
        kanbanId: createdKanbanViewId,
      });
      resetCreateCustomFieldRequest();
    }
  }, [
    createCustomFieldRequestStatus,
    createdKanbanViewId,
    getKanbanList,
    currentViewId,
    setSelectedKanbanCategoryId,
    resetCreateCustomFieldRequest,
    setSelectedKanbanCategoryByView,
  ]);

  // Sync fieldSortOrders with current filter when filters change externally
  useEffect(() => {
    if (filters.sortBy) {
      const sortByKey = String(filters.sortBy);
      const sortOrderValue = filters.sortOrder || SortOrder.ASC;
      setFieldSortOrders((prev) => {
        if (prev[sortByKey] === sortOrderValue) {
          return prev;
        }
        return {
          ...prev,
          [sortByKey]: sortOrderValue,
        };
      });
    }
  }, [filters.sortBy, filters.sortOrder]);

  useEffect(() => {
    if (!isSortByOpen) return;

    function handleClickOutside(e: MouseEvent) {
      const target = e.target as HTMLElement;

      // Ignore clicks inside the sort-by dropdown or transfer container
      if (
        target?.closest('.sort-by-options') ||
        target?.closest('.transfer-container')
      ) {
        return;
      }

      if (
        sortByButtonRef.current &&
        !sortByButtonRef.current.contains(e.target as Node)
      ) {
        setIsSortByOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSortByOpen]);

  useEffect(() => {
    sendGetProspectFiltersFieldsListRequest();
  }, []);

  useEffect(() => {
    if (
      previousSavedViewId !== undefined &&
      currentViewId !== previousSavedViewId
    ) {
      setSearchQuery('');
      setProspectFilters({
        ...filters,
        search: '',
        pageNum: 1,
        shouldFetchProspectList: false,
        shouldFetchProspectListCount: false,
      });
    }
    setPreviousSavedViewId(currentViewId);
  }, [currentViewId, previousSavedViewId, filters, setProspectFilters]);

  const openCategorizeByModal = () => {
    setCategorizeModalMode('create');
    setCategorizeModalInitialColumns([]);
    setSelectedCategory(null);
    setShowCategorizeByModal(true);
    setShowCategoryDropdown(false);
  };

  const handleEditCategory = useCallback((category: CategoryItem, context) => {
    const categoryPayload = category as KanbanProspectListPayload;
    setSelectedCategory(categoryPayload);
    setCategorizeModalMode('edit');

    if (categoryPayload?.metadata?.categories) {
      setCategorizeModalInitialColumns(
        categoryPayload?.metadata?.categories?.map((cat) => ({
          value: cat?.value,
          color: cat?.color,
          borderColor: cat?.color,
          isHidden: cat?.status !== 1,
        })),
      );
    } else {
      setCategorizeModalInitialColumns([]);
    }

    setShowCategoryDropdown(false);
    setShowCategorizeByModal(true);
  }, []);

  const handleSaveCategory = (
    columns: CategorizeByColumn[],
    title?: string,
  ) => {
    if (categorizeModalMode === 'create') {
      const payload = {
        label: title || 'New Category',
        fieldType: 'dropdown',
        prospectType: 'contact',
        fallbackText: '',
        metadata: {
          options: columns.map((col) => col.value),
        },
        colorOptions: columns.map((col) => col.color),
      };
      setAddNewCategoryTriggered(true);
      sendCreateCustomFieldRequest(payload);
      return;
    }

    if (categorizeModalMode === 'edit' && selectedCategory?.id) {
      const updatePayload = buildUpdatePayloadFromColumns({
        columns,
        selectedView: selectedCategory,
        title,
      });

      const originalCategories = selectedCategory?.metadata?.categories || [];
      const originalCategoryMap = new Map(
        originalCategories.map((cat) => [cat.value, cat]),
      );
      const unhiddenColumns: string[] = [];
      columns.forEach((column) => {
        const originalCategory = originalCategoryMap.get(column.value);
        if (
          originalCategory &&
          originalCategory.status === 0 &&
          !column.isHidden
        ) {
          unhiddenColumns.push(column.value);
        }
      });

      setUpdatedCategoryResponse({
        columns,
        updatedTitle: title,
        columnRenames: updatePayload?.columnRenames || [],
        unhiddenColumns: unhiddenColumns.length > 0 ? unhiddenColumns : [],
        updatedCategories: updatePayload?.metadata?.categories,
        kanbanViewId: selectedCategory.id,
      });

      updateKanbanView(selectedCategory.id, updatePayload);
    }
  };

  // Edit Card Modal handlers
  const onShowEditCardColumnModal = () => {
    setEditCardColumnModal(true);
  };

  const onHideEditCardColumnModal = useCallback(() => {
    setEditCardColumnModal(false);
  }, []);

  const onSetKanbanSelectedFieldsChange = async (
    updatedFields,
    newlyAddedColumns,
    toastMessage = '',
  ) => {
    dispatch(setSelectedKanbanCardsFields(updatedFields));
    onHideEditCardColumnModal();
  };

  useEffect(() => {
    if (selectedFieldsFromView?.length === 0) {
      return;
    }
    dispatch(setSelectedKanbanCardsFields(selectedFieldsFromView));
  }, [selectedFieldsFromView]);

  useEffect(() => {
    if (showTagsModal) {
      sendGetTagsRequest();
    }
  }, [showTagsModal, sendGetTagsRequest]);

  // Populate kanbanColumnGroups when fields are loaded
  useEffect(() => {
    executeOnRequestStatus({
      status: getFieldsRequestStatus,
      onSuccess: () => {
        if (fields && fields.length > 0) {
          setKanbanColumnGroups([
            {
              id: 'systemAttributes',
              title: 'System Attributes',
              fields: getKanbanProspectSystemAttributes(fields),
            },
            {
              id: 'CustomAttributes',
              title: 'Custom Attributes',
              fields: getProspectCustomAttributes(fields),
            },
          ]);
        }
      },
    });
  }, [getFieldsRequestStatus, fields]);

  // Handle savedNewColumn for kanban
  useEffect(() => {
    if (!savedNewColumn) return;

    const selectedFields = selectedKanbanCardsFields || [];

    // find if column already exists
    const existingFieldIndex = selectedFields.findIndex(
      (f) => f.id === savedNewColumn?.id?.toString(),
    );

    let updatedFields;

    if (existingFieldIndex !== -1) {
      updatedFields = [...selectedFields];
      updatedFields[existingFieldIndex] = {
        ...updatedFields[existingFieldIndex],
        fieldType: savedNewColumn?.fieldType,
        id: savedNewColumn?.id?.toString(),
        isEditable: true,
        label: savedNewColumn?.label,
      };
    } else {
      const newField = {
        fieldType: savedNewColumn?.fieldType,
        id: savedNewColumn?.id?.toString(),
        isEditable: true,
        label: savedNewColumn?.label,
      };

      updatedFields = [...selectedFields, newField];
    }

    onSetKanbanSelectedFieldsChange(
      updatedFields,
      [savedNewColumn],
      'Column updated successfully',
    );
    handleClearSelection();
  }, [savedNewColumn]);

  // Handle getProspectListByIdsRequestStatus for kanban columns
  useEffect(() => {
    executeOnRequestStatus({
      status: getProspectListByIdsRequestStatus,
      onSuccess: () => {
        if (isKanbanColumnsReset) {
          toaster.success('Set to default columns successfully');
          setIsKanbanColumnsReset(false);
        } else if (isNewKanbanColumnsAdded) {
          toaster.success('Columns updated successfully');
          setIsNewKanbanColumnsAdded(false);
        }
      },
    });
  }, [getProspectListByIdsRequestStatus]);

  // * Add Tags To Prospects Request
  useEffect(() => {
    executeOnRequestStatus({
      status: addTagsToProspectsRequestStatus,
      onSuccess: () => {
        handleClearSelection();
        handleUpdateProspectList({
          action: BulkActionProspect.TagsUpdated,
          fetchProspectList: true,
          fetchProspectListCount: false,
          resetPageNumber: false,
        });
        resetProspectFiltersFieldsAttributesValue({ key: 'tags' });
        resetTags();
        setShowTagsModal(false);
        toaster.success('Prospects Updated Successfully');
      },
      onFailed: () => {
        handleClearSelection();
        setShowTagsModal(false);
        resetTags();
      },
    });
  }, [addTagsToProspectsRequestStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: sendAddTagsToProspectsRequestStatus,
      onSuccess: () => {
        handleUpdateProspectList({
          action: BulkActionProspect.TagsUpdated,
          fetchProspectList: true,
          fetchProspectListCount: false,
          resetPageNumber: false,
        });
        handleClearSelection();
        resetProspectFiltersFieldsAttributesValue({ key: 'tags' });
        resetTags();
        resetBulkRequests();
        setIsBulkProspectSelected(false);
        setShowTagsModal(false);
        toaster.success(sendAddTagsToBulkProspectsRequestMessage, {
          delay: 3000,
        });
      },
      onFailed: () => {
        setShowTagsModal(false);
        handleClearSelection();
        resetBulkRequests();
        resetTags();
        toaster.error(sendAddTagsToBulkProspectsRequestError?.message, {
          delay: 5000,
        });
      },
    });
  }, [sendAddTagsToProspectsRequestStatus]);

  // * Remove Tags From Prospects Request
  useEffect(() => {
    executeOnRequestStatus({
      status: removeTagsFromProspectsRequestStatus,
      onSuccess: () => {
        handleClearSelection();
        handleUpdateProspectList({
          action: BulkActionProspect.TagsUpdated,
          fetchProspectList: true,
          fetchProspectListCount: doesFieldExistInConditions(
            filters.filterGroupLogic,
            'tags',
          ),
          resetPageNumber: false,
        });
        resetTags();

        setShowTagsModal(false);
        toaster.success('Prospects Updated Successfully');
      },
      onFailed: () => {
        notification.error({
          content: sendRemoveTagsFromBulkProspectsRequestError?.message,
          header: 'Oops',
          delay: 5000,
        });
        handleClearSelection();
        resetBulkRequests();
        resetTags();
        setShowTagsModal(false);
      },
    });
  }, [removeTagsFromProspectsRequestStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: sendRemoveTagsFromProspectsRequestStatus,
      onSuccess: () => {
        handleUpdateProspectList({
          action: BulkActionProspect.TagsUpdated,
          fetchProspectList: true,
          fetchProspectListCount: doesFieldExistInConditions(
            filters.filterGroupLogic,
            'tags',
          ),
          resetPageNumber: false,
        });
        handleClearSelection();
        resetTags();
        resetBulkRequests();
        setIsBulkProspectSelected(false);
        setShowTagsModal(false);
        toaster.success(sendRemoveTagsFromBulkProspectsRequestMessage, {
          delay: 3000,
        });
      },
      onFailed: () => {
        setShowTagsModal(false);
        handleClearSelection();
        resetBulkRequests();
        resetTags();
        toaster.error(sendRemoveTagsFromBulkProspectsRequestError?.message, {
          delay: 5000,
        });
      },
    });
  }, [sendRemoveTagsFromProspectsRequestStatus]);

  const handleClearSearch = () => {
    setProspectFilters({
      ...filters,
      search: '',
      pageNum: 1,
      shouldFetchProspectList: true,
      shouldFetchProspectListCount: true,
    });
    handleClearSelection();
    setSearchQuery('');
  };

  const getSearchIcons = (): InputIconConfig[] => {
    const icons: InputIconConfig[] = [
      {
        place: Input.IconPlace.Left,
        identifier: 'search',
      },
    ];

    if (searchQuery && searchQuery.length > 0) {
      icons.push({
        place: Input.IconPlace.Right,
        identifier: 'close-o cursor-pointer',
        onClick: handleClearSearch,
      });
    }

    return icons;
  };

  const handleKanbanCategorySelectionChange = useCallback(
    (selectedId: number | null) => {
      setSelectedKanbanCategoryId(selectedId);
      setSelectedCategoryViewId(currentViewId);
      // Persist selection per view so switching views restores the correct category
      setSelectedKanbanCategoryByView((prev) =>
        currentViewId !== null
          ? { ...prev, [currentViewId]: selectedId }
          : prev,
      );
      setCreatedKanbanViewForViewId((prev) =>
        prev.viewId === currentViewId
          ? { viewId: currentViewId, kanbanId: selectedId }
          : prev,
      );
    },
    [
      currentViewId,
      setSelectedKanbanCategoryId,
      setSelectedKanbanCategoryByView,
    ],
  );

  const defaultCategoryIdForActiveView = useMemo(() => {
    if (
      currentViewId !== null &&
      Object.prototype.hasOwnProperty.call(
        selectedKanbanCategoryByView,
        currentViewId,
      )
    ) {
      return selectedKanbanCategoryByView[currentViewId];
    }

    // Use the selected category only when it belongs to the current view
    if (selectedKanbanCategoryId && selectedCategoryViewId === currentViewId) {
      return selectedKanbanCategoryId;
    }

    if (
      createdKanbanViewForViewId.viewId === currentViewId &&
      createdKanbanViewForViewId.kanbanId
    ) {
      return createdKanbanViewForViewId.kanbanId;
    }

    if (selectedSavedView?.kanbanViewId) {
      const matched = getKanbanList?.find(
        (item) => item.id === selectedSavedView.kanbanViewId,
      );
      if (matched) return matched.id;
    }

    if (savedFilters?.length > 0) {
      let kanbanIdAssociatedWithActiveView = null;
      if (activeViewId) {
        kanbanIdAssociatedWithActiveView = savedFilters?.find(
          (item) => item.id === activeViewId,
        )?.kanbanViewId;
      } else {
        kanbanIdAssociatedWithActiveView = savedFilters?.find(
          (item) => item.filterName === 'All Prospects',
        )?.kanbanViewId;
      }
      const value =
        getKanbanList?.find(
          (item) => item.id === kanbanIdAssociatedWithActiveView,
        )?.id || null;

      return value;
    }

    return null;
  }, [
    getKanbanList,
    activeViewId,
    savedFilters,
    selectedSavedView,
    selectedKanbanCategoryId,
    selectedCategoryViewId,
    currentViewId,
    createdKanbanViewForViewId?.kanbanId,
    createdKanbanViewForViewId?.viewId,
    selectedKanbanCategoryByView,
  ]);

  const typeOfCategoriesList = useMemo(() => {
    const systemCategories =
      getKanbanList?.filter((item) => item.isDefault === 1) || [];
    const customCategories =
      getKanbanList?.filter((item) => item.isDefault === 0) || [];

    return {
      systemCategories,
      customCategories,
    };
  }, [JSON.stringify(getKanbanList || [])]);

  // Generate dynamic sort options based on currency and date fields
  const dynamicSortOptions = useMemo(
    () => getSortOptionsWithFields(fields || []),
    [fields],
  );

  const currentSortedOption = useMemo(() => {
    if (!filters.sortBy) {
      return null;
    }

    const sortByValue = filters.sortBy;
    const lookupKey = String(sortByValue);

    return (
      dynamicSortOptions?.find((option) => {
        const optionKey = String(option.key);

        // Exact match
        if (optionKey === lookupKey) {
          return true;
        }

        const isNumericMatch =
          /^\d+$/.test(optionKey) &&
          /^\d+$/.test(lookupKey) &&
          Number(optionKey) === Number(lookupKey);

        return isNumericMatch;
      }) || null
    );
  }, [filters.sortBy, dynamicSortOptions]);

  const tooltipText = useMemo(() => {
    if (filters.sortBy === ProspectSortBy.None) {
      return `Sorted by ${currentSortedOption.name}`;
    }
    if (isSortingActive && currentSortedOption) {
      return `Sorted by ${currentSortedOption.name}`;
    }
    return 'Sort by';
  }, [filters.sortBy, isSortingActive, currentSortedOption]);

  return (
    <div className="prospect-list-content--header--container">
      <div className="prospect-list-content--header">
        <div className="prospect-list-content--header__left">
          {savedFilters?.length !== 0 && (
            <>
              {/* <div className="prospect-list-content--header__divider" /> */}

              <div className="prospect-list-content--header__saved-filter">
                <SavedFilters
                  fetchProspectListAndCount={fetchProspectListAndCount}
                />
              </div>
            </>
          )}
        </div>

        <div className="prospect-list-content--header__right">
          <TriggerAiColumnButton />

          {hasPermission(Permissions.PROSPECT_UPDATE) && (
            <div className="add-prospect-button">
              <AddContactsButton
                fromModule={SHModules.Prospect}
                remainingProspectLimit={remainingProspectLimit}
              />
            </div>
          )}
        </div>

        <GlobalRestrictionModal
          show={showGlobalRestrictionModal}
          onClose={() => setShowGlobalRestrictionModal(false)}
          title={restrictionModalContentRef.current?.title}
          content={restrictionModalContentRef.current?.description}
          primaryBtnLabel="Sign up"
        />
      </div>
      <div className="prospect-list-content--header__bottom">
        <div className="prospect-list-content--header__bottom--left">
          <div className="prospect-view-switch">
            <OverlayTooltip text="List View">
              <span
                className={`prospect-list-view${
                  location?.pathname === '/crm' ||
                  location?.pathname === '/crm/list'
                    ? ' active-tab'
                    : ''
                }`}
                {...accessibleOnClick(() => {
                  // Reset filters when switching to list view
                  if (location?.pathname !== '/crm/list') {
                    resetFilterGroupLogic();
                    setSearchQuery('');
                    setProspectFilters({
                      ...filters,
                      filterGroupLogic: null,
                      pageNum: 1,
                      search: '',
                      shouldFetchProspectList: false,
                      shouldFetchProspectListCount: false,
                    });
                  }
                  setProspectViewIdFilter(null);

                  // Preserve query parameters if viewId exists
                  const urlParams = new URLSearchParams(location?.search || '');
                  const viewId = urlParams.get('viewId');
                  if (viewId) {
                    history.push({
                      pathname: '/crm/list',
                      search: location.search,
                      hash: location.hash,
                    });
                  } else {
                    history.push('/crm/list');
                  }
                })}
              >
                <ListViewIcon className="prospect-list-view-icon" />
                <span className="prospect-list-view-text">List</span>
              </span>
            </OverlayTooltip>
            <span
              className={`prospect-view-switch-divider ${
                location?.pathname === '/crm' ||
                location?.pathname === '/crm/list' ||
                location?.pathname === '/crm/kanban'
                  ? 'active-divider'
                  : ''
              }`}
            />
            <OverlayTooltip text="Kanban View">
              <span
                className={`prospect-kanban-view${
                  location?.pathname === '/crm/kanban' ? ' active-tab' : ''
                }`}
                {...accessibleOnClick(() => {
                  // Reset filters when switching to kanban view
                  if (location?.pathname !== '/crm/kanban') {
                    resetFilterGroupLogic();
                    setSearchQuery('');
                    setProspectFilters({
                      ...filters,
                      filterGroupLogic: null,
                      pageNum: 1,
                      search: '',
                      shouldFetchProspectList: false,
                      shouldFetchProspectListCount: false,
                    });
                  }
                  setProspectViewIdFilter(null);
                  // Preserve query parameters if viewId exists
                  const urlParams = new URLSearchParams(location?.search || '');
                  const viewId = urlParams.get('viewId');
                  if (viewId) {
                    history.push({
                      pathname: '/crm/kanban',
                      search: location.search,
                      hash: location.hash,
                    });
                  } else {
                    history.push('/crm/kanban');
                  }
                })}
              >
                <KanbanViewIcon className="prospect-kanban-view-icon" />
                <span className="prospect-kanban-view-text">Kanban</span>
                <div className="prospect-kanban-new-tag">
                  <span className="prospect-kanban-new-text">New</span>
                </div>
              </span>
            </OverlayTooltip>
          </div>
          <Input
            placeholder="Search Name or Email"
            icons={getSearchIcons()}
            value={searchQuery}
            onChange={(value) => setSearchQuery(value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                // Reset the locally set Redux filter state for prospect viewId filters
                setProspectViewIdFilter?.(null);
                setProspectFilters({
                  ...filters,
                  search: event.currentTarget.value,
                  pageNum: 1,
                  shouldFetchProspectList: true,
                  shouldFetchProspectListCount: true,
                });
                handleClearSelection();
              }
            }}
            className="prospect-list-content--header__bottom__search"
          />
          {(location?.pathname === '/crm' ||
            location?.pathname === '/crm/list') && (
            <>
              {/* <div className="prospect-list-content--header__divider" /> */}

              <BulkActions
                viewAddToSequenceModal={viewAddToSequenceModal}
                viewDeleteProspectModal={viewDeleteProspectModal}
                unsubscribeProspect={viewUnsubscribeProspectModal}
                viewEmailVerificationModal={handleEmailVerification}
                renderTagIcon={renderTagIcon}
                selectedRowIds={selectedRowIds}
                isBulkActionOnRows={isBulkActionOnRows}
              />
            </>
          )}
          {location?.pathname?.includes('/crm/kanban') &&
            defaultCategoryIdForActiveView && (
              <div className="categorize-by-outcome">
                <span className="categorize-by-outcome-text">
                  Categorize by
                </span>
                <CategoriesDropdown
                  systemCategories={typeOfCategoriesList?.systemCategories}
                  customCategories={typeOfCategoriesList?.customCategories}
                  onSelectionChange={handleKanbanCategorySelectionChange}
                  onAddNewCategory={openCategorizeByModal}
                  onEditCategory={handleEditCategory}
                  show={showCategoryDropdown}
                  setShow={() => setShowCategoryDropdown(!showCategoryDropdown)}
                  fetchProspectField={fields}
                  filters={filters}
                  sendGetProspectListRequest={sendGetProspectListRequest}
                  sendGetContactListByViewIdRequest={
                    sendGetContactListByViewIdRequest
                  }
                  sendGetProspectListCountRequest={
                    sendGetProspectListCountRequest
                  }
                  defaultCategoryIdForActiveView={
                    defaultCategoryIdForActiveView
                  }
                  setProspectViewIdFilter={setProspectViewIdFilter}
                />
              </div>
            )}
        </div>
        {showCategorizeByModal && (
          <DraggableInputModal
            show={showCategorizeByModal}
            onClose={() => setShowCategorizeByModal(!showCategorizeByModal)}
            initialColumns={categorizeModalInitialColumns}
            enableVisibilityToggle={categorizeModalMode === 'edit'}
            onSave={handleSaveCategory}
            initialTitle={
              selectedCategory?.categoryFieldId &&
              fields?.find(
                (field) => field.id === selectedCategory.categoryFieldId,
              )?.label
            }
            isDefault={selectedCategory?.isDefault}
            updateKanbanViewRequestStatus={
              categorizeModalMode === 'edit'
                ? updateKanbanViewRequestStatus
                : createCustomFieldRequestStatus
            }
            sendGetContactListByViewIdRequest={
              sendGetContactListByViewIdRequest
            }
            sendGetProspectListCountRequest={sendGetProspectListCountRequest}
            sendGetProspectListRequest={sendGetProspectListRequest}
            selectedKanbanCategoryId={selectedKanbanCategoryId}
            createCustomFieldRequestMessage={
              categorizeModalMode === 'create'
                ? createCustomFieldRequestMessage
                : 'Custom field updated successfully.'
            }
            setProspectViewIdFilter={setProspectViewIdFilter}
          />
        )}
        <div className="prospect-list-content--header__bottom--right">
          <div
            className={`prospect-list-content--header__filter-btn ${
              totalConditions ? 'selected' : ''
            }`}
            {...accessibleOnClick(viewFiltersModal)}
          >
            <div className="filter-icon">
              <AdvancedFilter />
            </div>
            <p>Filter</p>
            {totalConditions ? (
              <div className="count">{totalConditions}</div>
            ) : null}
          </div>
          {(location?.pathname === '/crm' ||
            location?.pathname === '/crm/list') && (
            <ExportButton handleExport={handleExport} />
          )}
          {location?.pathname === '/crm/kanban' && (
            <>
              <div
                ref={sortByButtonRef}
                className={`prospect-list-content--header__bottom--right--sort-btn ${
                  isSortingActive ? 'sorting-active' : ''
                }`}
              >
                <OverlayTooltip text={tooltipText}>
                  <div
                    className={`d-flex align-items-center ${
                      isSortingActive ? 'sorting-active-text' : ''
                    }`}
                    {...accessibleOnClick(handleSortByToggle)}
                  >
                    <ArrowsSort width={20} height={20} className="mr-1" />
                    <span>Sort By</span>
                  </div>
                </OverlayTooltip>
                {isSortByOpen && (
                  <SortByOptions
                    options={dynamicSortOptions}
                    sortOrder={filters.sortOrder}
                    currentSortBy={
                      filters.sortBy === ProspectSortBy.CreateAt
                        ? ProspectSortBy.CreateAt
                        : filters.sortBy || ProspectSortBy.None
                    }
                    fieldSortOrders={fieldSortOrders}
                    onSort={handleProspectSorting}
                    setProspectViewIdFilter={setProspectViewIdFilter}
                  />
                )}
              </div>
              <div
                className="prospect-list-content--header__bottom--right--edit"
                {...accessibleOnClick(onShowEditCardColumnModal)}
              >
                <div className="edit-icon-container">
                  <Edit className="edit-icon" />
                </div>
                <div className="prospect-list-content--header__bottom--right--edit-text">
                  Edit Card
                </div>
              </div>
            </>
          )}
          {/* <div className="prospect-list-content--header__bottom--right--help">
            <div className="help-icon-container">
              <Help className="help-icon" />
            </div>
            <div className="prospect-list-content--header__bottom--right--help-text">
              How It Works?
            </div>
          </div> */}
        </div>
      </div>
      {editCardColumnModal && (
        <EditCardColumnsModal
          show={editCardColumnModal}
          onSave={(updatedFields, newlyAddedColumns) => {
            const isAnyNewColumnAdded = newlyAddedColumns.length !== 0;

            onSetKanbanSelectedFieldsChange(
              updatedFields,
              newlyAddedColumns,
              'Columns updated successfully',
            );

            if (isAnyNewColumnAdded) {
              setIsNewKanbanColumnsAdded(true);
            }
          }}
          onCancel={onHideEditCardColumnModal}
          selectedFields={selectedKanbanCardsFields}
          columnGroups={kanbanColumnGroups}
          setProspectViewIdFilter={setProspectViewIdFilter}
        />
      )}
    </div>
  );
};

export default ProspectHeader;
